import {
  type AdvancedThreshold,
  type ThresholdColor,
  transformAdvancedThresholds,
  transformColorThresholds,
} from "@/overlay/components/CommonParts/Threshold/types";
import type { LabeledCoordinates } from "@/types/location";
import type { DatetimesWithOffset } from "@/utility/time";
import type {
  ElementStyle,
  WeatherTableColumnOutput,
  WeatherTableSchemaInput,
  WeatherTableSchemaOutput,
} from "@mm/metx-workbench.meteomatics.com";
import { DateTime } from "luxon";

export interface WeatherTableColumn {
  title: string;
  parameterUnit: string;
  model: string;
  show: boolean;
  index: number;
  calibrated: boolean;
  ensSelect: string;
  color: string;
  sort?: string;
  width?: number;
  filter?: any;
  thresholds: Array<ThresholdColor>;
  advancedThresholds: Array<AdvancedThreshold>;
  apiUrlSuffix?: string;
}

export function getWeatherTableColumnLabel(column: Pick<WeatherTableColumn, "title" | "parameterUnit">): string {
  return column.title?.trim() || column.parameterUnit;
}

export function getWeatherTableColumnCustomLabel(
  column: Pick<WeatherTableColumn, "title" | "parameterUnit">,
): string | null {
  const title = column.title?.trim();
  return title && title !== column.parameterUnit ? title : null;
}

export type WeatherTableGroupInfo = {
  title: string;
  show: boolean;
};

export type WeatherTableGroup = {
  groupInfo: WeatherTableGroupInfo;
  columns: WeatherTableColumn[];
};

export type WeatherTable = DatetimesWithOffset & {
  id: number;
  tabId: number;
  resolution: string;
  groups: WeatherTableGroup[];
  title: string;
  location: LabeledCoordinates;
  titleStyle?: ElementStyle;
  timeLock: boolean;
};

export const transformToWeatherTable = (tableSchema: WeatherTableSchemaOutput): WeatherTable => {
  return {
    id: tableSchema.id,
    tabId: tableSchema.id_tab,
    resolution: tableSchema.resolution || "PT1H",
    groups:
      tableSchema.groups?.map((group) => {
        return { groupInfo: group.group_info, columns: transformToWeatherTableColumns(group.columns) };
      }) || [],
    title: tableSchema.title || "Title",
    location: tableSchema.location_info
      ? {
          ...tableSchema.location_info.coordinates,
          label: tableSchema.location_info.label,
        }
      : {
          lat: tableSchema.location.lat,
          lon: tableSchema.location.lng,
        },
    titleStyle: tableSchema.titleStyle || undefined,
    timeLock: !!tableSchema.time_lock,
    datetimeFrom: tableSchema.datetime_from,
    datetimeFromOffset: tableSchema.datetime_from_offset,
    datetimeTo: tableSchema.datetime_to,
    datetimeToOffset: tableSchema.datetime_to_offset,
  };
};

export const transformToWeatherTableColumns = (columns: Array<WeatherTableColumnOutput>): WeatherTableColumn[] => {
  if (!columns) {
    return [];
  }
  return columns.map((column) => {
    return {
      title: column.title,
      parameterUnit: column.parameter_unit,
      model: column.model,
      show: column.show,
      index: column.index,
      calibrated: column.calibrated,
      ensSelect: column.ens_select || "",
      color: column.color,
      sort: column.sort || undefined,
      filter: column.filter,
      width: column.width || undefined,
      thresholds: transformColorThresholds(column.thresholds),
      advancedThresholds: transformAdvancedThresholds(column.advanced_thresholds),
      apiUrlSuffix:
        typeof (column as { api_url_suffix?: string | null }).api_url_suffix === "string"
          ? (column as { api_url_suffix?: string | null }).api_url_suffix || undefined
          : undefined,
    };
  });
};

export const transformToWeatherTableColumnOutput = (columns: Array<WeatherTableColumn>): WeatherTableColumnOutput[] => {
  if (!columns) {
    return [];
  }
  return columns.map((column) => {
    return {
      title: column.title,
      parameter_unit: column.parameterUnit,
      model: column.model,
      show: column.show,
      index: column.index,
      calibrated: column.calibrated,
      ens_select: column.ensSelect,
      color: column.color,
      sort: column.sort || undefined,
      filter: column.filter,
      width: column.width || undefined,
      thresholds: column.thresholds,
      advanced_thresholds: column.advancedThresholds,
      api_url_suffix: column.apiUrlSuffix || undefined,
    } as WeatherTableColumnOutput;
  });
};

export const transformToWeatherTableInput = (tableSchema: WeatherTable): WeatherTableSchemaInput => {
  return {
    time_created: DateTime.now().toISO(),
    time_updated: DateTime.now().toISO(),
    id: tableSchema.id,
    id_tab: tableSchema.tabId,
    resolution: tableSchema.resolution,
    groups:
      tableSchema.groups?.map((group) => {
        return {
          group_info: group.groupInfo,
          columns: transformToWeatherTableColumnOutput(group.columns),
        };
      }) || [],
    title: tableSchema.title || "Title",
    location: tableSchema.location
      ? { lat: tableSchema.location.lat, lng: tableSchema.location.lon }
      : { lat: 0, lng: 0 }, // why cant we have lon or lng everywhere
    location_info: {
      coordinates: {
        lat: tableSchema.location.lat,
        lon: tableSchema.location.lon,
      },
      label: tableSchema.location.label ?? null,
    },
    titleStyle: tableSchema.titleStyle ?? null,
    time_lock: tableSchema.timeLock,
    datetime_from: tableSchema.datetimeFrom,
    datetime_from_offset: tableSchema.datetimeFromOffset,
    datetime_to: tableSchema.datetimeTo,
    datetime_to_offset: tableSchema.datetimeToOffset,
  };
};
